import React, { useEffect, useRef, useState } from 'react'
import {
  getCachedPeaks,
  getWaveformPeaks,
  DEFAULT_PEAK_BUCKETS,
} from '@/lib/waveformPeaks'
import { useAllSamples } from '@/hooks/useSampleUpload'

/**
 * Canvas-based waveform preview for a single sample. Used inside
 * pad tiles + the PadRow label cell in PadEditor so the user can
 * visually identify what's loaded without triggering playback.
 *
 * Sources its peaks from `lib/waveformPeaks` which caches decoded
 * results by sampleId. First render triggers a fetch + decode;
 * subsequent renders (same sample) draw from the cache synchronously.
 *
 * Colour is inherited from the `color` prop so pad tiles can tint
 * the waveform to match their focus / kind state (white on a
 * focused accent pad, tertiary on a neutral pad, etc.). Default
 * falls back to the accent colour.
 */

interface WaveformThumbnailProps {
  sampleId: string
  /** Width in CSS pixels — affects how many peak buckets we
   *  actually draw. The decoded peak array is 128 buckets; at
   *  narrow widths we subsample visually, at wider widths we
   *  stretch. */
  width: number
  /** Height in CSS pixels. Mapped 1:1 to canvas height. */
  height: number
  /** CSS colour string for the waveform strokes. Defaults to a
   *  CSS custom property so the thumbnail themes with the app. */
  color?: string
  /** Optional className forwarded to the canvas element for
   *  additional styling (borders, rounded corners). */
  className?: string
}

export const WaveformThumbnail: React.FC<WaveformThumbnailProps> = ({
  sampleId,
  width,
  height,
  color = 'currentColor',
  className = '',
}) => {
  const { data: samples = [] } = useAllSamples()
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [peaks, setPeaks] = useState<Float32Array | null>(() =>
    getCachedPeaks(sampleId),
  )

  // Kick off a decode when the cache misses. The effect cancels
  // via an `active` flag so a rapid sampleId swap doesn't set
  // peaks for a stale sample.
  useEffect(() => {
    const cached = getCachedPeaks(sampleId)
    if (cached) {
      setPeaks(cached)
      return
    }
    const sample = samples.find(s => s.id === sampleId)
    if (!sample) {
      setPeaks(null)
      return
    }
    let active = true
    void getWaveformPeaks(sample).then(result => {
      if (!active) return
      setPeaks(result)
    })
    return () => {
      active = false
    }
  }, [sampleId, samples])

  // Render the waveform when peaks + canvas are available. We use
  // a canvas because the per-bucket line-draw pattern is faster +
  // crisper than SVG at small sizes.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio : 1
    canvas.width = width * dpr
    canvas.height = height * dpr
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.scale(dpr, dpr)
    ctx.clearRect(0, 0, width, height)
    if (!peaks || peaks.length === 0) return
    const buckets = peaks.length / 2
    const mid = height / 2
    ctx.fillStyle = color
    // One filled vertical rect per bucket — fast + antialiases
    // cleanly on high-DPR screens. Bucket width collapses to a
    // minimum of 1 px so very small thumbnails still read.
    const bucketWidth = Math.max(width / buckets, 1)
    for (let b = 0; b < buckets; b++) {
      const min = peaks[b * 2] ?? 0
      const max = peaks[b * 2 + 1] ?? 0
      const y1 = mid - max * mid
      const y2 = mid - min * mid
      const rectY = Math.min(y1, y2)
      const rectH = Math.max(1, Math.abs(y2 - y1))
      ctx.fillRect(
        b * bucketWidth,
        rectY,
        Math.max(bucketWidth - 0.5, 0.5),
        rectH,
      )
    }
  }, [peaks, width, height, color])

  // `title` includes the sampleId so a user who suspects two pads
  // are sharing a thumbnail (cache mix-up) can hover + confirm the
  // ids actually differ. Cheap diagnostic that costs no runtime.
  return (
    <canvas
      ref={canvasRef}
      style={{ width, height }}
      className={className}
      aria-hidden
      title={`Sample ${sampleId.slice(0, 8)}`}
    />
  )
}

/** Re-exported for callers that want to pick their own bucket
 *  count via the raw peak helpers. */
export { DEFAULT_PEAK_BUCKETS }
